import type { AxiosInstance } from 'axios';

export interface CommunityProfile {
  displayName: string;
  /** Über den exakten Benutzernamen auffindbar. Voreinstellung seit V200: an. */
  discoverable: boolean;
  username: string;
  /** Persönlicher Einladungslink; wer ihn annimmt, ist sofort befreundet. */
  inviteUrl: string | null;
}
/** Vorschau eines Einladungslinks. `state` fehlt für Gäste und beim eigenen Link. */
export interface CommunityInvite { firstName: string; own: boolean; state: FriendshipState | null }
export interface CommunitySettingsRequest { discoverable: boolean }
export type FriendshipState = 'NONE' | 'INCOMING' | 'OUTGOING' | 'FRIEND' | 'BLOCKED';
export interface CommunityPerson { id: number; displayName: string; state: FriendshipState }
export type EventInterestStatus = 'GOING';
export type InterestVisibility = 'PRIVATE' | 'FRIENDS';
export interface EventInterest { status: EventInterestStatus; visibility: InterestVisibility }
/**
 * Was ein bestätigter Freund mit dem Event vorhat: zugesagt (`GOING`) oder vorgemerkt
 * (`INTERESTED`, aus seinem Lesezeichen, seit 30.10.2026). Wer zugesagt hat, kommt nur als Zusage.
 */
export interface FriendInterest { id: number; displayName: string; status: EventInterestStatus | 'INTERESTED' }
export interface EventCommunity { interest: EventInterest | null; friends: FriendInterest[] }
export interface CommunityOverview { profile: CommunityProfile | null; people: CommunityPerson[] }
export const FRIENDSHIP_LABELS: Record<FriendshipState, string> = {
  NONE: 'Anfragen', INCOMING: 'Bestätigen', OUTGOING: 'Anfrage zurückziehen', FRIEND: 'Freundschaft entfernen', BLOCKED: 'Blockierung aufheben',
};
const vorname = (f: FriendInterest) => f.displayName.trim().split(/\s+/)[0];
/** „Lena“, „Lena und Tom“, „Lena und 2 weitere“. */
function namen(list: FriendInterest[]) {
  if (list.length === 1) return vorname(list[0]);
  if (list.length === 2) return `${vorname(list[0])} und ${vorname(list[1])}`;
  return `${vorname(list[0])} und ${list.length - 1} weitere`;
}
/**
 * Freunde am Event in zwei Teilen, damit die Oberfläche sie verschieden zeichnen kann: wer dabei
 * ist, steht mit Namen vorn; wer nur vorgemerkt hat, kommt dahinter, neben einer Zusage nur als
 * Zahl („Lena dabei, 2 vorgemerkt“). Die Zeile ist schmal, deshalb nie mehr als zwei Namen.
 */
export function friendInterestParts(friends: FriendInterest[]) {
  const going = friends.filter(f => f.status === 'GOING');
  const interested = friends.filter(f => f.status === 'INTERESTED');
  const dabei = going.length ? `${namen(going)} dabei` : '';
  const vorgemerkt = !interested.length ? ''
    : going.length && interested.length > 1 ? `${interested.length} vorgemerkt`
    : `${namen(interested)} vorgemerkt`;
  return { going, interested, dabei, vorgemerkt };
}
export function friendInterestText(friends: FriendInterest[]) {
  const { dabei, vorgemerkt } = friendInterestParts(friends);
  return [dabei, vorgemerkt].filter(Boolean).join(', ');
}
export function createCommunityService(api: AxiosInstance) {
  return {
    planning: async (view: 'mine' | 'friends'): Promise<import('./types').CyclingEvent[]> => (await api.get('/community/events', { params: { view } })).data,
    overview: async (): Promise<CommunityOverview> => (await api.get('/community')).data,
    saveSettings: async (settings: CommunitySettingsRequest): Promise<CommunityOverview> => (await api.put('/community/settings', settings)).data,
    renewInvite: async (): Promise<CommunityOverview> => (await api.post('/community/invite/renew')).data,
    invite: async (code: string): Promise<CommunityInvite> => (await api.get(`/community/invites/${encodeURIComponent(code)}`)).data,
    acceptInvite: async (code: string): Promise<CommunityInvite> => (await api.post(`/community/invites/${encodeURIComponent(code)}/accept`)).data,
    search: async (q: string): Promise<CommunityPerson[]> => (await api.get('/community/people', { params: { q } })).data,
    act: async (id: number, action: 'request' | 'accept' | 'remove' | 'block'): Promise<CommunityOverview> => (await api.post(`/community/friends/${id}/${action}`)).data,
    event: async (id: number): Promise<EventCommunity> => (await api.get(`/community/events/${id}`)).data,
    saveInterest: async (id: number, interest: EventInterest | null): Promise<EventCommunity> =>
      interest ? (await api.put(`/community/events/${id}`, interest)).data : (await api.delete(`/community/events/${id}`)).data,
  };
}

export function teamFriendAction(state: FriendshipState): 'request' | 'accept' | 'remove' | null {
  return state === 'NONE' ? 'request' : state === 'INCOMING' ? 'accept' : state === 'OUTGOING' ? 'remove' : null;
}
export const TEAM_FRIEND_LABELS: Record<FriendshipState, string> = {
  NONE: 'Als Freund hinzufügen', INCOMING: 'Freundschaft bestätigen', OUTGOING: 'Anfrage gesendet. Zurückziehen', FRIEND: 'Bereits befreundet', BLOCKED: 'Nicht verfügbar',
};

export const PLANNER_VIEWS = [
  { id: 'mine', label: 'Meine Events', empty: 'Noch keine Events', hint: 'Merke dir Events mit dem Lesezeichen vor.' },
  { id: 'friends', label: 'Freunde', empty: 'Noch keine Events deiner Freunde', hint: 'Hier erscheinen die Events deiner Freunde.' },
] as const;
export type PlannerView = typeof PLANNER_VIEWS[number]['id'];
export const plannerView = (value: unknown): PlannerView => value === 'friends' ? value : 'mine';

export function plannerEventsAfterChange(events: import('./types').CyclingEvent[], id: number, patch: Partial<Pick<import('./types').CyclingEvent, 'favorited' | 'participating'>>, view: PlannerView) {
  return events.map(event => event.id === id ? { ...event, ...patch } : event)
    .filter(event => view !== 'mine' || event.favorited || event.participating);
}

export function communityGroups(people: CommunityPerson[]) {
  return {
    friends: people.filter(person => person.state === 'FRIEND'),
    incoming: people.filter(person => person.state === 'INCOMING'),
    outgoing: people.filter(person => person.state === 'OUTGOING'),
    blocked: people.filter(person => person.state === 'BLOCKED'),
  };
}
export const communityInitials = (name: string) => name.trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase();

/** Suchtreffer behalten nach einer Aktion ihren Platz und zeigen den neuen Beziehungsstatus. */
export function communitySearchResultsAfterUpdate(results: CommunityPerson[] | null, overview: CommunityOverview, changedId: number) {
  return results?.map(person => person.id === changedId ? { ...person, state: overview.people.find(item => item.id === changedId)?.state ?? ('NONE' as FriendshipState) } : person)
    .filter(person => person.state !== 'BLOCKED') ?? null;
}

/** Freundesliste im Planer, aufgeklappt. Ziel nach dem Annehmen einer Einladung. */
export const FRIENDS_LIST_PATH = '/favorites?view=friends&verwalten=1';

/** Pfad der Einladungsseite im Web, passend zu `CommunityService.inviteUrl` im Backend. */
export const friendInvitePath = (code: string) => `/freunde/einladung/${encodeURIComponent(code)}`;

/** Texte rund um den Einladungslink. Web und App lesen dieselben. */
export const FRIEND_INVITE_TEXTS = {
  title: 'Freunde einladen',
  lead: 'Teile deinen Link mit Freunden. Sobald sie die Einladung annehmen, seid ihr auf Radsport Events befreundet.',
  copy: 'Link kopieren',
  copied: 'Kopiert',
  share: 'Link teilen',
  renew: 'Einladungslink erneuern',
  renewConfirm: 'Dein bisheriger Einladungslink wird ungültig. Bestehende Freundschaften bleiben erhalten.',
  username: (username: string) => `Dein Benutzername: ${username}`,
  shareMessage: (url: string) => `Lass uns auf Radsport Events vernetzen und sehen, welche Events wir vorhaben: ${url}`,
  searchPlaceholder: 'Benutzername',
  searchHint: 'Gib den vollständigen Benutzernamen ein. Groß- und Kleinschreibung spielt keine Rolle.',
  discoverable: 'Über meinen Benutzernamen auffindbar',
  discoverableHint: 'Wenn ausgeschaltet, können andere dich nicht über die Suche finden. Dein Einladungslink funktioniert weiterhin.',
  searchEmpty: 'Keine Person gefunden. Prüfe den Benutzernamen oder lass dir einen Einladungslink schicken.',
};

/** Die Einladungsseite: wer einlädt, und warum sich die Freundschaft lohnt. */
export function friendInviteIntro(firstName: string) {
  return {
    kicker: 'Einladung',
    title: `${firstName} lädt dich ein`,
    lead: 'Entdeckt, bei welchen Radsport-Events ihr euch treffen könnt.',
    points: [
      'Ihr seht gegenseitig, welche Events ihr vorgemerkt habt und wo ihr dabei seid.',
      'Außer euren Freunden sieht das niemand.',
      'Du kannst die Freundschaft jederzeit beenden.',
    ],
    accept: 'Einladung annehmen',
    login: 'Anmelden und Einladung annehmen',
    loginHint: 'Du meldest dich mit einem Code per E-Mail an. Hast du noch kein Konto, wird dabei eines erstellt.',
    friends: `Du bist mit ${firstName} befreundet.`,
    own: 'Teile deinen Einladungslink mit Freunden, mit denen du bei Radsport-Events dabei sein möchtest.',
    toFriends: 'Events deiner Freunde ansehen',
    notFound: 'Diese Einladung ist nicht verfügbar. Bitte die Person, die dich eingeladen hat, um einen neuen Link.',
    signedInAs: (account: string) => `Angemeldet als ${account}`,
    switchAccount: 'Konto wechseln',
  };
}
